import React, { useState, useEffect, useMemo } from 'react';
import { 
    Target, TrendingUp, AlertTriangle, 
    CheckCircle, XCircle, Info,
    Loader2, Search, Database, Crosshair, ShieldAlert, Swords, Scan, Copy,
    Scale, Download, Trophy, Skull
} from 'lucide-react';

// Shared UI
import { 
    AppCard, AppButton 
} from '../../components/SharedUI';

// Services
import { generateDetailedCompetitorAnalysisLLM } from '../../services/aiService';

// --- STYLED COMPONENTS ---

const ComparisonCard = ({ title, value, type }) => {
    const colors = {
        good: "text-emerald-400 border-emerald-500/30 bg-emerald-900/10",
        bad: "text-red-400 border-red-500/30 bg-red-900/10",
        neutral: "text-zinc-300 border-zinc-700 bg-zinc-900/50"
    };
    const style = colors[type] || colors.neutral;

    return (
        <div className={`flex flex-col items-center justify-center p-3 rounded-xl border ${style}`}>
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-1">{title}</span>
            <span className="text-lg font-black font-mono text-center leading-tight">{value || "N/A"}</span>
        </div>
    );
};

// --- PREMIUM OVERLAY COMPONENT ---
const PremiumIntelOverlay = ({ isVisible, currentStepIndex }) => {
    if (!isVisible) return null;

    const steps = [
        "Acquiring Target Profile...",
        "Scanning Historical Box Office Data...",
        "Deconstructing Marketing Spend...",
        "Identifying Audience Overlap...",
        "Formulating Counter-Strategy..."
    ];

    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-red-600 blur-2xl opacity-20 animate-pulse rounded-full"></div>
                        <Swords className="w-20 h-20 text-white relative z-10 animate-pulse" />
                    </div>
                </div>

                <div className="w-full h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-red-600 to-orange-500 transition-all duration-300 ease-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => {
                        const isCurrent = index === currentStepIndex;
                        const isDone = index < currentStepIndex;
                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${isCurrent ? 'text-white font-bold' : isDone ? 'text-red-500/50' : 'text-zinc-600'}`}>
                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isCurrent ? 'border-white bg-white/10' : isDone ? 'border-red-500 bg-red-500/20' : 'border-zinc-800'}`}>
                                    {isDone && <CheckCircle size={10} />}
                                    {isCurrent && <Loader2 size={10} className="animate-spin" />}
                                </div>
                                <span>{step}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---

const CompetitiveIntelTab = ({ filmData, setNotification, saveFilmData, credits, deductCredit }) => {
    // Safely access initial title
    const [competitorTitle, setCompetitorTitle] = useState(filmData.competitor_analysis?.title || "");
    const [loading, setLoading] = useState(false);
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);

    // Safely access analysis object
    const analysis = useMemo(() => filmData.competitor_analysis?.analysis || null, [filmData.competitor_analysis]);

    // Check if the data is from the OLD version (missing 'tale_of_the_tape')
    const isLegacyData = analysis && !analysis.tale_of_the_tape;

    useEffect(() => {
        if(filmData.competitor_analysis?.title) setCompetitorTitle(filmData.competitor_analysis.title);
    }, [filmData]);

    // --- PROGRESS SIMULATION ---
    useEffect(() => {
        let interval;
        if (loading) {
            setLoadingStepIndex(0);
            interval = setInterval(() => {
                setLoadingStepIndex(prev => (prev < 4 ? prev + 1 : prev));
            }, 1500); 
        }
        return () => clearInterval(interval);
    }, [loading]);

    const handleAnalyze = async () => {
        if (!competitorTitle) {
            setNotification({ type: 'error', message: 'Please enter a competitor film title.' });
            return;
        }
        const creditCost = 2; 
        if (credits < creditCost) {
            setNotification({ type: 'error', message: `Insufficient credits. Requires ${creditCost}.` });
            return;
        }

        setLoading(true);
        try {
            const result = await generateDetailedCompetitorAnalysisLLM(competitorTitle, filmData);
            
            setLoadingStepIndex(4);
            await new Promise(r => setTimeout(r, 500));

            await deductCredit(creditCost);
            await saveFilmData({
                ...filmData,
                competitor_analysis: {
                    title: competitorTitle,
                    analysis: result
                }
            });
            setNotification({ type: 'success', message: 'Target analysis acquired.' });
        } catch (error) {
            console.error(error);
            setNotification({ type: 'error', message: `Analysis failed: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    // --- PDF EXPORT ---
    const handleExportPDF = () => {
        try {
            if (!window.jspdf || !analysis) {
                setNotification({ type: 'error', message: 'PDF generator not ready.' });
                return;
            }
            
            // Safety check for export
            if (!analysis.tale_of_the_tape) {
                 setNotification({ type: 'error', message: 'Data format incompatible with export. Please re-run analysis.' });
                 return;
            }

            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;
            let y = margin;

            // HELPER: Auto-Page Break
            const checkPageBreak = (neededHeight) => {
                if (y + neededHeight > pageHeight - margin) {
                    doc.addPage();
                    y = margin;
                }
            };

            // Header
            doc.setFillColor(24, 24, 27);
            doc.rect(0, 0, pageWidth, 100, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(22);
            doc.setFont("helvetica", "bold");
            doc.text("COMPETITIVE INTELLIGENCE", margin, 60);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.text(`${filmData.title?.toUpperCase()}  VS.  ${competitorTitle.toUpperCase()}`, margin, 80);

            y = 140;
            doc.setTextColor(0, 0, 0);

            // Tale of Tape
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text("TALE OF THE TAPE", margin, y);
            y += 20;
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            
            if (analysis.tale_of_the_tape) {
                Object.entries(analysis.tale_of_the_tape).forEach(([k, v]) => {
                    doc.text(`${k.replace(/_/g, ' ').toUpperCase()}: ${v}`, margin, y);
                    y += 15;
                });
            }
            y += 20;

            // Forensic
            checkPageBreak(60);
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text("FORENSIC ANALYSIS", margin, y);
            y += 20;
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            
            if (analysis.forensic_analysis) {
                const killShot = doc.splitTextToSize(`THE KILL SHOT: ${analysis.forensic_analysis.the_kill_shot}`, pageWidth - (margin * 2));
                doc.text(killShot, margin, y);
                y += killShot.length * 15 + 10;
                
                const missing = doc.splitTextToSize(`OUR ADVANTAGE: ${analysis.forensic_analysis.missing_ingredient}`, pageWidth - (margin * 2));
                checkPageBreak(missing.length * 15);
                doc.text(missing, margin, y);
                y += missing.length * 15 + 30;
            }

            // Battle Plan - FIXED WRAPPING
            if (analysis.battle_plan) {
                checkPageBreak(100);
                doc.setFillColor(220, 38, 38); // Red
                doc.rect(margin, y, pageWidth - (margin * 2), 30, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.text("BATTLE PLAN", margin + 10, y + 20);
                
                y += 50;
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                
                // Helper to print wrapped text
                const printWrapped = (label, text) => {
                    const fullText = `${label}: ${text}`;
                    const lines = doc.splitTextToSize(fullText, pageWidth - (margin * 2));
                    checkPageBreak(lines.length * 15);
                    doc.text(lines, margin, y);
                    y += lines.length * 15 + 10;
                };

                printWrapped("STEAL", analysis.battle_plan.steal_this);
                printWrapped("EXPLOIT", analysis.battle_plan.exploit_this);
                printWrapped("IGNORE", analysis.battle_plan.ignore_this);
            }

            doc.save(`${filmData.title}_Vs_${competitorTitle}.pdf`);
            setNotification({ type: 'success', message: 'Intel Dossier exported.' });
        } catch (e) {
            console.error(e);
            setNotification({ type: 'error', message: 'PDF export failed.' });
        }
    };

    return (
        <div className="h-full flex flex-col space-y-8 pb-20">
            <PremiumIntelOverlay isVisible={loading} currentStepIndex={loadingStepIndex} />

            {/* --- TOP: WAR ROOM HEADER --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-zinc-800 pb-6 gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center border border-zinc-800">
                        <Crosshair size={24} className="text-red-500" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white font-poppins tracking-tight">War Room</h2>
                        <p className="text-xs text-zinc-500 uppercase tracking-widest">Competitive Forensics & Counter-Strategy</p>
                    </div>
                </div>
                
                {/* Search Bar */}
                <div className="flex w-full md:w-auto gap-2">
                    <div className="relative flex-grow md:w-64">
                        <input 
                            type="text" 
                            value={competitorTitle} 
                            onChange={e => setCompetitorTitle(e.target.value)} 
                            placeholder="Enter Comp Title..."
                            className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 rounded-lg focus:outline-none focus:border-red-500 transition-colors text-sm font-bold uppercase tracking-wide"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                    </div>
                    <AppButton 
                        onClick={handleAnalyze} 
                        disabled={loading} 
                        className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        icon={Scan}
                    >
                        {loading ? 'Scanning...' : 'Analyze'}
                    </AppButton>
                </div>
            </div>

            {/* --- EMPTY STATE --- */}
            {!analysis && !loading && (
                <div className="flex-1 flex flex-col items-center justify-center opacity-40 text-center">
                    <div className="w-32 h-32 border-2 border-dashed border-zinc-700 rounded-full flex items-center justify-center mb-6">
                        <Target size={48} className="text-zinc-600" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Awaiting Target</h3>
                    <p className="text-zinc-500 mt-2">Enter a film title to deconstruct its strategy.</p>
                </div>
            )}

            {/* --- LEGACY DATA WARNING --- */}
            {isLegacyData && !loading && (
                <div className="p-6 bg-yellow-900/20 border border-yellow-500/30 rounded-xl flex items-center gap-4">
                    <AlertTriangle className="text-yellow-500 w-8 h-8" />
                    <div>
                        <h4 className="text-yellow-400 font-bold">Outdated Intelligence Format</h4>
                        <p className="text-yellow-200/60 text-sm">
                            This report uses an older data structure. Please click <strong>Analyze</strong> above to re-run the forensic scan with the new War Room engine.
                        </p>
                    </div>
                </div>
            )}

            {/* --- ANALYSIS DISPLAY --- */}
            {analysis && !isLegacyData && !loading && (
                <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 space-y-8">
                    
                    {/* 1. TALE OF THE TAPE (HEAD TO HEAD) */}
                    <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                            <Scale size={120} />
                        </div>
                        <div className="flex justify-between items-center mb-6 relative z-10">
                            <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                                <Scale size={16} className="text-purple-500" /> Tale of the Tape
                            </h3>
                            <button onClick={handleExportPDF} className="text-zinc-500 hover:text-white transition-colors" title="Export PDF">
                                <Download size={18} />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 relative z-10">
                            <ComparisonCard 
                                title="Verdict" 
                                value={analysis.tale_of_the_tape?.box_office_verdict} 
                                type={analysis.tale_of_the_tape?.box_office_verdict?.includes('Hit') ? 'good' : 'bad'} 
                            />
                            <ComparisonCard title="Est. Budget" value={analysis.tale_of_the_tape?.budget_estimated} type="neutral" />
                            <ComparisonCard title="Opening Wknd" value={analysis.tale_of_the_tape?.opening_weekend} type="neutral" />
                            <ComparisonCard title="Critics" value={analysis.tale_of_the_tape?.critic_score} type="neutral" />
                            <ComparisonCard title="Audience" value={analysis.tale_of_the_tape?.audience_score} type="neutral" />
                        </div>
                    </div>

                    {/* 2. FORENSIC DECONSTRUCTION */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-red-900/10 to-transparent border border-red-500/20 p-6 rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <Skull size={20} className="text-red-500" /> The Kill Shot
                            </h3>
                            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
                                {analysis.forensic_analysis?.the_kill_shot}
                            </p>
                            <div className="bg-black/30 p-3 rounded-lg border border-red-500/10">
                                <p className="text-[10px] font-bold text-red-400 uppercase mb-1">Their Weakness</p>
                                <p className="text-zinc-400 text-xs">{analysis.swot_comparison?.their_weakness}</p>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-emerald-900/10 to-transparent border border-emerald-500/20 p-6 rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <Trophy size={20} className="text-emerald-500" /> Our Advantage
                            </h3>
                            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
                                {analysis.forensic_analysis?.missing_ingredient}
                            </p>
                            <div className="bg-black/30 p-3 rounded-lg border border-emerald-500/10">
                                <p className="text-[10px] font-bold text-emerald-400 uppercase mb-1">Strategic Opportunity</p>
                                <p className="text-zinc-400 text-xs">{analysis.swot_comparison?.our_opportunity}</p>
                            </div>
                        </div>
                    </div>

                    {/* 3. BATTLE PLAN */}
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                        <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-6">Battle Plan Directives</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <div className="flex items-center gap-2 mb-2 text-blue-400 font-bold text-xs uppercase tracking-wide">
                                    <Copy size={14} /> Steal This
                                </div>
                                <p className="text-zinc-300 text-sm">{analysis.battle_plan?.steal_this}</p>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-2 text-green-400 font-bold text-xs uppercase tracking-wide">
                                    <Target size={14} /> Exploit This
                                </div>
                                <p className="text-zinc-300 text-sm">{analysis.battle_plan?.exploit_this}</p>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-2 text-red-400 font-bold text-xs uppercase tracking-wide">
                                    <ShieldAlert size={14} /> Ignore This
                                </div>
                                <p className="text-zinc-300 text-sm">{analysis.battle_plan?.ignore_this}</p>
                            </div>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default CompetitiveIntelTab;